<template>
  <div>
    <InfoCard>
      I Navn & adresse kan du se og endre informasjon som utleierprofilens navn og faktura- og postadresse.
    </InfoCard>

    <div class="row mt-4">
      <div class="col-12 col-md-6">
        <BlockContainer class="p-5 h-100">
          <ValidationObserver v-slot="{ invalid, dirty, reset, validate }">
            <h5>Utleierprofil</h5>

            <hl-SwitchWithLabels
              class="my-4 mt-5"
              v-model="landlordPrivate"
              onState="Privatperson"
              offState="Virksomhet"
              @input="validate"
            />

            <div v-if="!landlordPrivate" key="company">
              <ValidationProvider
                name="Navn"
                :rules="validationRules.landlordProfile.landlordInfo.fullName"
                v-slot="{ errors }"
              >
                <hl-Input placeholder="Linda Brukersen" label="Navn" v-model="landlord.name" :error="errors[0]" />
              </ValidationProvider>

              <ValidationProvider
                name="Organisasjonsnummer"
                :rules="validationRules.landlordProfile.landlordInfo.orgNumber"
                v-slot="{ errors }"
                key="1"
              >
                <hl-Input
                  label="Organisasjonsnummer"
                  placeholder="Org.nr"
                  v-model="landlord.organizationNumber"
                  :error="errors[0]"
                  class="mt-3"
                />
              </ValidationProvider>

              <div class="d-flex justify-content-end mt-4">
                <button
                  class="btn btn-md btn-primary"
                  :disabled="invalid || !dirty || savingLandlord"
                  @click="saveLandlordProfile(reset)"
                >
                  Lagre informasjon
                </button>
              </div>
            </div>

            <div v-else key="private">
              <div class="d-flex align-items-center mb-3">
                <div>
                  <a-Label class="landlordName">Navn</a-Label>
                  <p>{{ landlord.name }}</p>
                </div>
                <Icon
                  :name="iconName"
                  :size="iconSize"
                  class="ml-3"
                  :class="[hasLandlordIdentitySet ? 'text-primary' : 'text-danger']"
                />
              </div>

              <aside v-if="hasLandlordIdentitySet" class="verifiedInfo">
                <div class="ml-3">
                  <p class="mb-1">
                    <strong>Hvis du ønsker å endre identiteten på utleierprofilen din</strong>
                  </p>
                  <p>
                    Kontakt kundeservice for å tilbakestille identiteten på utleierprofilen din. Dette gir deg
                    muligheten til å verifisere profilen på nytt ved bruk av BankID.
                  </p>
                </div>
              </aside>

              <div v-else>
                <p>
                  <strong>Verifisering av utleierprofil</strong>
                </p>

                <p>
                  Ettersom du forvalter dine leieforhold gjennom oss, er det viktig at vi blir litt bedre kjent med deg.
                </p>

                <p>
                  Når vi har en sikker identitet på både utleier og leietaker, kan vi skape tryggere leieforhold og
                  tilby dere en bedre og sikrere tjeneste.
                </p>

                <p>Trykk på knappen under for å starte prosessen.</p>

                <a :href="verificationAppURL" class="btn btn-primary" target="_blank"> Verifiser </a>
              </div>
            </div>

            <hl-Toast
              v-if="patchLandlordProfileMessage !== undefined"
              :title="patchLandlordProfileMessage.title"
              :text="patchLandlordProfileMessage.message"
              :variant="patchLandlordProfileMessage.type"
              class="mt-5"
            />
          </ValidationObserver>
        </BlockContainer>
      </div>

      <div class="col-12 col-md-6">
        <BlockContainer>
          <ValidationObserver ref="postAddressForm" v-slot="{ valid, invalid, dirty }">
            <div class="row p-5">
              <div class="col">
                <h5>Post- og fakturaadresse</h5>
              </div>

              <div class="col text-right">
                <SaveButton @click.native="saveLandlordAddresses()" :disabled="invalid || !dirty"></SaveButton>
              </div>
            </div>

            <div>
              <ul class="custom-tabs nav nav-tabs" id="myTab" role="tablist">
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: isPostAddressTabActive }" @click="toggleActiveTab('post')"
                    >Post</a
                  >
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: !isPostAddressTabActive }" @click="toggleActiveTab('invoicing')"
                    >Faktura</a
                  >
                </li>
              </ul>

              <div class="tab-content pl-5 pr-5 pb-5 pt-3" id="myTabContent">
                <div v-if="isPostAddressTabActive" class="mt-4 tab-pane fade show active">
                  <ValidationProvider name="C/O">
                    <hl-Input label="C/O" placeholder="Ola Nordmann" v-model="landlordPostalAddress.careOf"></hl-Input>
                  </ValidationProvider>

                  <ValidationProvider
                    name="Adresse"
                    :rules="validationRules.landlordProfile.communicationSettings.street"
                    v-slot="{ errors }"
                  >
                    <hl-Input
                      class="mt-4"
                      label="Adresse"
                      placeholder="St Olavs Plass 2"
                      v-model="landlordPostalAddress.street"
                      :error="errors[0]"
                    ></hl-Input>
                  </ValidationProvider>

                  <div class="row mt-4">
                    <div class="col">
                      <ValidationProvider
                        name="Postnummer"
                        :rules="validationRules.landlordProfile.communicationSettings.postalZipCode"
                        v-slot="{ errors }"
                      >
                        <hl-Input
                          label="Postnummer"
                          placeholder="0481"
                          v-model="landlordPostalAddress.postalZipcode"
                          @input="fetchPostalArea(arguments[0], 'landlordPostalAddress')"
                          :error="errors[0]"
                        ></hl-Input>
                      </ValidationProvider>
                    </div>

                    <div class="col">
                      <ValidationProvider
                        name="Poststed"
                        :rules="validationRules.landlordProfile.communicationSettings.postalArea"
                        v-slot="{ errors }"
                      >
                        <hl-Input
                          label="Poststed"
                          placeholder="Oslo"
                          disabled
                          v-model="landlordPostalAddress.postalArea"
                          :error="errors[0]"
                        ></hl-Input>
                      </ValidationProvider>
                    </div>
                  </div>

                  <ValidationProvider
                    name="E-post"
                    :rules="validationRules.landlordProfile.communicationSettings.email"
                    v-slot="{ errors }"
                  >
                    <hl-Input
                      class="mt-4"
                      placeholder="e-post"
                      label="E-post"
                      v-model="landlordPostalAddress.email"
                      :error="errors[0]"
                    ></hl-Input>
                  </ValidationProvider>
                </div>

                <!-- Faktura Address -->
                <div v-else class="mt-4 tab-pane fade show active">
                  <ValidationProvider name="C/O">
                    <hl-Input label="C/O" placeholder="Ola Nordmann" v-model="landlordBillingAddress.careOf"></hl-Input>
                  </ValidationProvider>

                  <ValidationProvider name="Adresse">
                    <hl-Input
                      class="mt-4"
                      label="Adresse"
                      placeholder="St Olavs Plass 2"
                      v-model="landlordBillingAddress.street"
                    ></hl-Input>
                  </ValidationProvider>

                  <div class="row mt-4">
                    <div class="col">
                      <ValidationProvider name="Postnummer" :rules="'numeric'" v-slot="{ errors }">
                        <hl-Input
                          label="Postnummer"
                          placeholder="0481"
                          v-model="landlordBillingAddress.postalZipcode"
                          @input="fetchPostalArea(arguments[0], 'landlordBillingAddress')"
                          :error="errors[0]"
                        ></hl-Input>
                      </ValidationProvider>
                    </div>

                    <div class="col">
                      <ValidationProvider name="Poststed">
                        <hl-Input
                          label="Poststed"
                          placeholder="Oslo"
                          disabled
                          v-model="landlordBillingAddress.postalArea"
                        ></hl-Input>
                      </ValidationProvider>
                    </div>
                  </div>

                  <ValidationProvider
                    name="E-post"
                    :rules="validationRules.landlordProfile.communicationSettings.email"
                    v-slot="{ errors }"
                  >
                    <hl-Input
                      class="mt-4"
                      placeholder="e-post"
                      label="E-post"
                      v-model="landlordBillingAddress.email"
                      :error="errors[0]"
                    ></hl-Input>
                  </ValidationProvider>
                </div>
                <!-- / Faktura Address -->
              </div>
            </div>
          </ValidationObserver>
        </BlockContainer>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BlockContainer from '@/components/BlockContainer.vue';
import addValidationRules from '@/mixins/addValidationRules.js';
import SaveButton from '@/components/UI/buttons/SaveButton';
import postalCodeLookup from '@/lib/postalCodeLookup';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import InputSecret from '@modules/husleie-framework/src/components/forms/inputs/input/InputSecret.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import SwitchWithLabels from '@modules/husleie-framework/src/components/forms/inputs/switch/SwitchWithLabels.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import InfoCard from '@/components/InfoCard.vue';
import Toast from '@modules/husleie-framework/src/components/Toast.vue';
import Label from '@modules/design-system/components/atoms/labels/Label.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import { LandlordApi } from '@modules/husleieapi2';
import Icon, { IconName, IconSize } from '@modules/design-system/components/Icon';

export default {
  mixins: [i18nCommon, addValidationRules],

  components: {
    BlockContainer,
    SaveButton,
    InfoCard,
    'hl-Modal': Modal,
    'hl-InputSecret': InputSecret,
    'hl-Input': Input,
    'hl-Button': Button,
    'hl-SwitchWithLabels': SwitchWithLabels,
    'hl-Toast': Toast,
    'a-Label': Label,
    Icon,
  },

  data() {
    return {
      landlordPrivate: false,
      landlord: {
        addresses: [],
      },
      savingLandlord: false,
      landlordProfileUserToBeRemoved: null,
      isPostAddressTabActive: true,
      patchLandlordProfileMessage: undefined,
      hasLandlordIdentitySet: undefined,
    };
  },

  computed: {
    ...mapGetters(['getLandlordId']),

    landlordPostalAddress: {
      get() {
        return this.landlord.addresses.find(adr => adr.purpose === 'postal') || {};
      },
      set(val) {
        console.log('set', val);
        val.purpose = 'postal';
        const i = this.landlord.addresses.findIndex(adr => adr.purpose === 'postal');
        if (i >= 0) {
          this.landlord.addresses.splice(i, 1, val);
        } else {
          this.landlord.addresses.push(val);
        }
      },
    },
    landlordBillingAddress: {
      get() {
        return this.landlord.addresses.find(adr => adr.purpose === 'billing') || {};
      },
      set(val) {
        val.purpose = 'billing';
        const i = this.landlord.addresses.findIndex(adr => adr.purpose === 'billing');
        if (i >= 0) {
          this.landlord.addresses.splice(i, 1, val);
        } else {
          this.landlord.addresses.push(val);
        }
      },
    },
    verificationAppURL() {
      return RedirectOperations.getURL(AppRedirect.BANKID_VERIFICATION);
    },
    iconName() {
      return IconName.InterfaceVerified;
    },
    iconSize() {
      return IconSize.Large;
    },
  },

  methods: {
    ...mapActions([
      'removeAccessToUser',
      'loadLandlordProfile',
      'patchLandlordProfile',
      'loadLandlordProfileUsers',
      'patchLandlordProfile',
    ]),

    async fetchPostalArea(postalCode, addressPropertyName) {
      if (postalCode.length !== 4) {
        return;
      }

      try {
        const clonedAddress = JSON.parse(JSON.stringify(this[addressPropertyName]));
        const res = await postalCodeLookup(postalCode);
        clonedAddress.postalArea = res.poststed;
        this[addressPropertyName] = clonedAddress;
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke hente poststed. Sjekk at postnummer er gyldig format.', 'error');
      }
    },
    async saveLandlordProfile(callback) {
      this.savingLandlord = true;
      this.patchLandlordProfileMessage = undefined;

      try {
        const resp = await this.patchLandlordProfile({
          name: this.landlord.name,
          organizationNumber: this.landlord.organizationNumber,
        });
        this.patchLandlordProfileMessage = {
          type: 'success',
          title: this.translate('landlordProfile.nameAndAddress.org.success.title'),
          message: this.translate('landlordProfile.nameAndAddress.org.success.message'),
        };
        this.landlord.name = resp.name;
      } catch {
        this.patchLandlordProfileMessage = {
          type: 'error',
          title: this.translate('landlordProfile.nameAndAddress.org.error.title'),
          message: this.translate('landlordProfile.nameAndAddress.org.error.message'),
        };
      }

      if (callback) {
        callback();
      }
      this.savingLandlord = false;
    },
    toggleActiveTab(tab) {
      if (tab === 'post') {
        this.isPostAddressTabActive = true;
      } else {
        this.isPostAddressTabActive = false;
      }
    },
    async saveLandlordAddresses() {
      try {
        await this.patchLandlordProfile({
          addresses: this.landlord.addresses,
        });
        this.$toast('Lagret', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Noe gikk galt.', 'error');
      }
    },
    async fetchLandlordIdentity() {
      try {
        const api = new LandlordApi();
        await api.getLandlordIdentity(this.getLandlordId);

        this.hasLandlordIdentitySet = true;
      } catch (error) {
        if (error.response.status !== 404) {
          throw new Error('Failed calling `getLandlordIdentity`');
        }
        this.hasLandlordIdentitySet = false;
      }
    },
  },
  async mounted() {
    try {
      const res = await this.loadLandlordProfile();
      this.landlord = res;

      if (!this.landlord.nationalIdentityNumber) {
        this.landlord.nationalIdentityNumber = '';
      }

      if (!this.landlord.addresses.find(adr => adr.purpose === 'postal')) {
        this.landlord.addresses.push({
          purpose: 'postal',
          careOf: '',
          email: '',
          postalArea: '',
          postalZipcode: '',
          street: '',
        });
      }

      if (!this.landlord.addresses.find(adr => adr.purpose === 'billing')) {
        this.landlord.addresses.push({
          purpose: 'billing',
          careOf: '',
          email: '',
          postalArea: '',
          postalZipcode: '',
          street: '',
        });
      }

      this.$nextTick(() => {
        if (res.organizationNumber) {
          this.landlordPrivate = false;
        } else {
          this.landlordPrivate = true;
        }
        this.$refs.postAddressForm.validate();
      });
    } catch (error) {
      console.error(error);
      this.$toast('Kunne ikke laste utleierprofil.', 'error');
    }

    try {
      await this.loadLandlordProfileUsers();
    } catch (error) {
      console.error(error);
      this.$toast('Kunne ikke laste brukere.', 'error');
    }

    if (this.landlordPrivate) {
      this.fetchLandlordIdentity();
    }
  },
};
</script>

<style scoped lang="scss">
.custom-tabs {
  border-top: 1px solid $gray-300;
  border-bottom: 1px solid $gray-300;
  padding: 3px 1.5rem 0 1.5rem;
  box-shadow: $box-shadow-2;

  .nav-tabs {
    border-bottom: 0;
    font-weight: 400;
  }
  .nav-link {
    padding: 1rem 2rem;
    color: $secondary;
  }

  .nav-item:not(.active):hover {
    cursor: pointer;
  }

  .active {
    border: none;
    border-bottom: 3px solid $primary;
    color: $primary;
  }
}
.landlordName {
  font-size: 0.8em;
  color: $husleie-text-light;
}
.verifiedInfo {
  border-left: 2px solid black;
}
</style>
